/**
 * Admin view: this workspace, rendered inside the admin portal, for somebody who is not the
 * candidate.
 *
 * The requirement is that an admin sees the workspace *as the candidate sees it* — same app,
 * same styling, same data. That is why the admin portal frames this application rather than
 * reimplementing it: a port across the stack boundary would be a second copy of the workspace
 * that drifts from this one every time either changes, and a drifting copy fails the actual
 * requirement. So there is one workspace, and this module is the seam it is opened through.
 *
 * WHY THE TOKEN ARRIVES BY postMessage
 *
 * The frame is cross-origin: the admin portal is a different origin from this app, so the
 * admin's Firebase session — which lives in that origin's IndexedDB — is not readable here, and
 * this origin has no session of its own for them. The token therefore has to be handed across,
 * and there are only two ways to do it.
 *
 * Putting it in the iframe URL is the easy one and the wrong one: URLs are logged by servers and
 * proxies, kept in history, and handed to anything the page later links to as a referrer. A
 * bearer token in a query string is a bearer token in a log file.
 *
 * So it comes over `postMessage`, which is point-to-point and never serialised anywhere: the
 * frame announces itself to its parent, the parent replies with a token, and both sides check
 * the other's origin against a fixed allow-list on every message. The token is held in a module
 * variable and never written to `localStorage`, `sessionStorage` or a cookie, so it cannot
 * outlive the frame, cannot be read by another tab of this origin, and leaves nothing behind
 * when the admin closes the view.
 *
 * WHAT THIS DOES NOT DO
 *
 * It grants nothing. Every check that matters is on the server: the token is verified there, the
 * uid is looked up in `admin-users` there, the trial is pinned there, and every write is refused
 * there. This module's job is only to carry a credential and to declare which trial is being
 * viewed. A tampered value here buys nothing that the server would not refuse.
 */
import env from './environments';

/** The URL shape the admin portal frames. The trial id is a path segment, never a query param. */
const ADMIN_VIEW_PATH = /^\/admin-view\/([^/?#]+)/;

/**
 * The one origin allowed to hand this frame a token, and the only one it will speak to.
 *
 * A single configured origin rather than a list: this frame is opened by the admin portal and by
 * nothing else, and an allow-list with room in it is an allow-list somebody eventually widens.
 * `postMessage` is given this value literally rather than `"*"`, so the token is delivered to the
 * intended origin or not at all — with `"*"` a page that had navigated its opener elsewhere would
 * still receive it.
 */
export const ADMIN_PORTAL_ORIGIN = env.adminPortalOrigin || '';

const MESSAGE_SOURCE = 'appli-admin-view';
export const MESSAGE_TYPES = Object.freeze({
    READY: 'ready',
    TOKEN_REQUEST: 'token-request',
    TOKEN: 'token',
});

/**
 * Held in memory, deliberately. See the module note: persisting an admin's credential in an
 * origin the admin does not otherwise use is the one thing this seam must not do.
 */
let state = { active: false, workTrialId: '', token: '' };

/** Resolvers waiting on the parent's next token delivery. */
let pending = [];
let listening = false;

/**
 * The trial this page is an admin view of, or `""`.
 *
 * Read from `window.location` rather than from router state because the API client installs its
 * interceptors at module load, long before React has mounted a route — and the first workspace
 * read must already carry the header.
 */
export const adminViewTrialIdFromPath = (pathname) => {
    const match = ADMIN_VIEW_PATH.exec(
        pathname || (typeof window !== 'undefined' ? window.location.pathname : '') || '');
    return match ? decodeURIComponent(match[1]) : '';
};

/** Whether this page load is an admin view at all. False for every candidate session. */
export const isAdminView = () => state.active;

/** The trial being viewed, or `""` when this is not an admin view. */
export const adminViewTrialId = () => (state.active ? state.workTrialId : '');

/** The admin's ID token, or `""` before the parent has delivered one. */
export const adminViewToken = () => (state.active ? state.token : '');

const isTrustedOrigin = (origin) =>
    Boolean(ADMIN_PORTAL_ORIGIN) && origin === ADMIN_PORTAL_ORIGIN;

const post = (type, payload = {}) => {
    if (typeof window === 'undefined' || window.parent === window) return;
    if (!ADMIN_PORTAL_ORIGIN) return;
    window.parent.postMessage({ source: MESSAGE_SOURCE, type, ...payload }, ADMIN_PORTAL_ORIGIN);
};

const settle = (token) => {
    const waiting = pending;
    pending = [];
    waiting.forEach((resolve) => resolve(token));
};

const handleMessage = (event) => {
    // Both halves are checked. The origin says the message came from the admin portal; the source
    // tag says it is one of ours rather than a stray message from some other library sharing the
    // same frame relationship.
    if (!isTrustedOrigin(event.origin)) return;
    const data = event.data;
    if (!data || data.source !== MESSAGE_SOURCE) return;

    if (data.type === MESSAGE_TYPES.TOKEN) {
        state.token = typeof data.token === 'string' ? data.token : '';
        settle(state.token);
    }
};

/**
 * Enter admin view for this page load, and open the handshake.
 *
 * Idempotent: the route component may mount more than once (a remount, a screen change) and the
 * listener and the announcement must not be duplicated.
 */
export const beginAdminView = (workTrialId) => {
    if (!workTrialId) return;
    state.active = true;
    state.workTrialId = workTrialId;

    if (typeof window === 'undefined' || listening) return;
    listening = true;
    window.addEventListener('message', handleMessage);
    post(MESSAGE_TYPES.READY, { work_trial_id: workTrialId });
};

/**
 * The admin's current token, waiting for the parent to deliver one if it has not yet.
 *
 * Every request goes through this rather than reading the cached value, because an admin's ID
 * token expires after an hour and this view is meant to be left open. Asking the parent — which
 * holds the real Firebase session — is the only way to get a fresh one, so a request that arrives
 * after expiry triggers a new delivery instead of failing.
 *
 * The timeout is what stops a workspace hanging on a parent that never answers: a frame opened
 * outside the admin portal, or an admin portal too old to know this protocol, resolves to `""`
 * and the request goes out unauthenticated, to be refused by the server with a 401 the view can
 * report. Waiting forever would render as a blank stage with no explanation.
 */
export const adminViewTokenAsync = ({ refresh = false, timeoutMs = 5000 } = {}) => {
    if (!state.active) return Promise.resolve('');
    if (state.token && !refresh) return Promise.resolve(state.token);

    return new Promise((resolve) => {
        let done = false;
        const finish = (token) => {
            if (done) return;
            done = true;
            resolve(token || '');
        };
        pending.push(finish);
        post(MESSAGE_TYPES.TOKEN_REQUEST, { work_trial_id: state.workTrialId });
        setTimeout(() => finish(state.token), timeoutMs);
    });
};

/** Test seam. Nothing in the app calls this; it exists so a spec can reset module state. */
export const __resetAdminViewForTests = () => {
    if (typeof window !== 'undefined' && listening) {
        window.removeEventListener('message', handleMessage);
    }
    state = { active: false, workTrialId: '', token: '' };
    pending = [];
    listening = false;
};

/**
 * The header that declares the mode to the candidate API and names the trial being viewed.
 *
 * Exported from here rather than spelled at the call site because it is half of a contract with
 * `candidate-portal-backend/src/utils/admin_view.py`, and a contract written down in two places
 * is one that eventually disagrees with itself.
 */
export const ADMIN_VIEW_HEADER = 'X-Appli-Admin-View';

/**
 * The URL root this page's workspace is mounted under.
 *
 * `/workspace` is the candidate's own; `/admin-view` is the identical screen tree opened by an
 * admin. Every link the workspace builds goes through this, so a click inside a framed admin view
 * stays inside it rather than navigating the frame to a candidate URL there is no session for.
 *
 * Derived from module state rather than from the current path because the mode is set once, at
 * boot, before anything renders — and unlike a path read it cannot be wrong halfway through a
 * navigation. A session never moves between the two roots, so this is stable for the page's life.
 */
export const WORKSPACE_ROOT_CANDIDATE = '/workspace';
export const WORKSPACE_ROOT_ADMIN_VIEW = '/admin-view';

export const workspaceRoot = () =>
    (isAdminView() ? WORKSPACE_ROOT_ADMIN_VIEW : WORKSPACE_ROOT_CANDIDATE);
